import isEmpty from 'lodash/isEmpty';
import { type ReactNode, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  applyImpersonationState,
  decodeImpersonationLink,
  IMPERSONATION_LINK_PARAM,
  isRoleRevelioAdmin,
  selectImpersonationActive,
  useGetLoggedInUser,
} from '@revelio/auth';
import { Loading } from '@revelio/core';

import styles from './ImpersonationLinkGate.module.css';
import { markImpersonationSource } from './impersonation-source';

/**
 * Applies an `?impersonation=` link param (admins only) then strips it — on every
 * render, not just mount, since this gate stays mounted across client-side
 * navigation. `isProcessing` blocks children until the param is stripped.
 */
function useImpersonationLink(): { isProcessing: boolean } {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    realLoggedInUser,
    query: [{ fetching: isFetchingUser }],
  } = useGetLoggedInUser();

  const encoded = searchParams.get(IMPERSONATION_LINK_PARAM);

  useEffect(() => {
    // Wait for a param and for the user query to settle (success or failure).
    if (!encoded || isFetchingUser) {
      return;
    }

    const decoded = decodeImpersonationLink(encoded);
    if (
      decoded &&
      !isEmpty(realLoggedInUser) &&
      isRoleRevelioAdmin(realLoggedInUser.role)
    ) {
      // Named before the store flips so the tracking hook can attribute the
      // start to the link rather than the modal.
      if (selectImpersonationActive(decoded)) markImpersonationSource('link');
      applyImpersonationState(decoded);
    }

    // Strip the param. `flushSync: true` commits the URL synchronously —
    // otherwise the router defers via startTransition and children re-mount on
    // the stale URL, re-adding the param via their filter-sync hooks. Read
    // `window.location` live so concurrent URL writes aren't clobbered.
    const liveUrl = new URL(window.location.href);
    liveUrl.searchParams.delete(IMPERSONATION_LINK_PARAM);
    navigate(liveUrl.pathname + liveUrl.search, {
      replace: true,
      flushSync: true,
    });
  }, [encoded, isFetchingUser, realLoggedInUser, navigate]);

  return { isProcessing: encoded !== null };
}

export function ImpersonationLinkGate({ children }: { children: ReactNode }) {
  const { isProcessing } = useImpersonationLink();

  if (isProcessing) {
    return (
      <div className={styles.gate}>
        <Loading />
      </div>
    );
  }

  return <>{children}</>;
}
